import React from "react";
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input, Spinner } from "reactstrap";

export const TITLE_NEED_GATHER_BEFORE_CAPABILITIES = "Get current config for this DVR before loading capabilities.";

function formatCurrentResolution(cam) {
  if (!cam || typeof cam !== "object") return "—";
  const w = cam.width;
  const h = cam.height;
  const hasW = w !== null && w !== undefined && Number.isFinite(Number(w));
  const hasH = h !== null && h !== undefined && Number.isFinite(Number(h));
  if (hasW && hasH) return `${w}×${h}`;
  if (hasW) return String(w);
  if (hasH) return String(h);
  return "—";
}

function formatCurrentScalar(value) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string" && value.trim() === "") return "—";
  return String(value);
}

function renderFieldParagraph(label, valueText) {
  return (
    <p className="mb-1">
      <strong>{label}:</strong> <span>{valueText}</span>
    </p>
  );
}

// rtsp_verification is tri-state: true (checked, ok), false (checked, failed), null (not checked).
// Surface the reason inline when the device supplied one.
function formatRtspVerification(cam) {
  const v = cam?.rtsp_verification;
  const reason = cam?.rtsp_verification_reason;
  const label = v === true ? "Verified" : v === false ? "Failed" : "—";
  if (reason != null && String(reason).trim() !== "") return `${label} (${reason})`;
  return label;
}

// Read-only "Current configuration" fields from the gather (cameras) response. No /set-* endpoint
// backs these, so they render as plain paragraphs rather than editors.
function CurrentConfigParagraphs({ cam }) {
  return (
    <>
      {renderFieldParagraph("Resolution", formatCurrentResolution(cam))}
      {renderFieldParagraph("Fps", formatCurrentScalar(cam?.fps))}
      {renderFieldParagraph("Bitrate", formatCurrentScalar(cam?.bitrate))}
      {renderFieldParagraph("Video encoding", formatCurrentScalar(cam?.video_encoding))}
      {renderFieldParagraph("Bitrate control", formatCurrentScalar(cam?.bitrate_control))}
      {renderFieldParagraph("RTSP path", formatCurrentScalar(cam?.rtsp_path))}
      {renderFieldParagraph("RTSP port", formatCurrentScalar(cam?.device_rtsp_port))}
      {renderFieldParagraph("Motion detection", formatCurrentScalar(cam?.motion_detect))}
      {renderFieldParagraph("Encryption", formatCurrentScalar(cam?.encrypt))}
      {renderFieldParagraph("IP proxy", formatCurrentScalar(cam?.is_ip_proxy))}
      {renderFieldParagraph("RTSP verification", formatRtspVerification(cam))}
    </>
  );
}

function CurrentBadge({ value, label }) {
  if (value == null || value === "") return null;
  return (
    <Badge color="info" pill className="ml-2" aria-label={`Current ${label}: ${value}`}>
      Current: {value}
    </Badge>
  );
}

function PerFieldStatus({ result, label, formatApplied }) {
  if (!result) return null;
  if (result.error) {
    return <p className="small text-danger mb-0 mt-1">{result.error}</p>;
  }
  if (result.verified === true) {
    return <p className="small text-success mb-0 mt-1">Applied {formatApplied(result.applied)} (verified)</p>;
  }
  return (
    <p className="small text-warning mb-0 mt-1">
      Submitted {label} — not verified by DVR; refresh current config to confirm.
    </p>
  );
}

function buildResolutionOptions(supported, deviceResolution) {
  const list = Array.isArray(supported) ? [...supported] : [];
  if (deviceResolution && !list.includes(deviceResolution)) {
    return [
      { value: deviceResolution, label: `${deviceResolution} (current, not selectable)`, disabled: true },
      ...list.map((v) => ({ value: v, label: v })),
    ];
  }
  return list.map((v) => ({ value: v, label: v }));
}

function buildFpsOptions(supported, deviceFps) {
  const list = Array.isArray(supported) ? supported.map(String) : [];
  if (deviceFps && !list.includes(deviceFps)) {
    return [
      { value: deviceFps, label: `${deviceFps} (current, not selectable)`, disabled: true },
      ...list.map((v) => ({ value: v, label: v })),
    ];
  }
  return list.map((v) => ({ value: v, label: v }));
}

function UnavailableCapabilityRow({ label, ariaLabel }) {
  return (
    <div className="pt-2">
      <h6 className="text-muted text-uppercase small mb-2">{label}</h6>
      <Input type="select" bsSize="sm" style={{ maxWidth: "8rem" }} disabled aria-label={ariaLabel}>
        <option value="">—</option>
      </Input>
      <p className="small text-muted mb-0 mt-1">Not advertised by device</p>
    </div>
  );
}

function encodingNotAdvertised(stream) {
  if (!stream || typeof stream !== "object") return false;
  const encodings = stream.supported_encodings;
  return !Array.isArray(encodings) || encodings.length === 0;
}

function bitrateControlNotAdvertised(stream) {
  if (!stream || typeof stream !== "object") return false;
  const modes = stream.supported_bitrate_control_modes;
  return !Array.isArray(modes) || modes.length === 0;
}

function buildEncodingOptions(supported, deviceEncoding) {
  if (!Array.isArray(supported) || supported.length === 0) return [];
  const seen = new Set();
  const dedup = supported
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter((v) => v !== "" && (seen.has(v) ? false : seen.add(v)));
  if (deviceEncoding && !dedup.includes(deviceEncoding)) {
    return [
      { value: deviceEncoding, label: `${deviceEncoding} (current, not selectable)`, disabled: true },
      ...dedup.map((v) => ({ value: v, label: v })),
    ];
  }
  return dedup.map((v) => ({ value: v, label: v }));
}

function buildModeOptions(supported, deviceMode) {
  const list = Array.isArray(supported) ? supported.map((m) => String(m).toLowerCase()) : [];
  if (deviceMode && !list.includes(deviceMode)) {
    return [
      { value: deviceMode, label: `${deviceMode.toUpperCase()} (current, not selectable)`, disabled: true },
      ...list.map((v) => ({ value: v, label: v.toUpperCase() })),
    ];
  }
  return list.map((v) => ({ value: v, label: v.toUpperCase() }));
}

/** Shared Card class list for stream capability cards (live and degraded placeholders). */
export function streamCardClassName({ isConfiguredStream = false } = {}) {
  return [
    "mb-2",
    "w-100",
    "h-100",
    "video-recorder-capabilities__stream-card",
    "d-flex",
    "flex-column",
    isConfiguredStream ? "video-recorder-capabilities__stream-card--configured" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export function ChannelStreamCard({
  channelNumber,
  channelName,
  streamType,
  stream,
  currentStream,
  onGetCapabilities,
  streamLoading,
  dashcamBusy,
  gatherLoaded,
  draft,
  deviceValues,
  dirtyFields,
  onDraftChange,
  onApply,
  onReset,
  applyResult,
  invalidFields,
  peerMissing,
  isConfiguredStream = false,
}) {
  const namePart = channelName ? ` — ${channelName}` : "";
  const title = `Channel ${channelNumber}${namePart} (${streamType})`;

  const hasCaps = Boolean(stream);
  const applying = Boolean(applyResult?.loading);
  const dirty = dirtyFields ?? new Set();
  const isDirty = dirty.size > 0;
  const fieldsDisabled = applying || dashcamBusy || streamLoading;
  const invalid = invalidFields ?? {};
  const hasInvalid = Object.keys(invalid).length > 0;

  const resolutionOptions = hasCaps
    ? buildResolutionOptions(stream.supported_resolutions, deviceValues?.resolution)
    : [];
  const fpsOptions = hasCaps ? buildFpsOptions(stream.supported_fps, deviceValues?.fps) : [];
  const modeOptions = hasCaps
    ? buildModeOptions(stream.supported_bitrate_control_modes, deviceValues?.bitrate_control)
    : [];
  const encodingOptions = hasCaps ? buildEncodingOptions(stream.supported_encodings, deviceValues?.encoding) : [];
  const showEncodingUnavailable = hasCaps && encodingNotAdvertised(stream);
  const showBitrateControlUnavailable = hasCaps && bitrateControlNotAdvertised(stream);
  const minBitrate = stream?.min_bitrate;
  const maxBitrate = stream?.max_bitrate;
  const hasBitrateBounds = Number.isFinite(minBitrate) && Number.isFinite(maxBitrate);
  const showEditors = hasCaps && Boolean(draft);

  return (
    <Card className={streamCardClassName({ isConfiguredStream })}>
      <CardHeader>
        <CardTitle className="text-center" tag="h5">
          {title}
        </CardTitle>
      </CardHeader>
      <CardBody className="d-flex flex-column flex-grow-1 mt-2">
        <div className="mb-3">
          <h6 className="text-muted text-uppercase small mb-2">Current configuration</h6>
          {gatherLoaded && !currentStream ? (
            <p className="small text-warning mb-0">DVR did not return current config for this channel/stream.</p>
          ) : (
            <>
              {gatherLoaded && peerMissing ? (
                <p className="small text-warning mb-2">
                  DVR did not return current config for the main stream of this channel.
                </p>
              ) : null}
              <CurrentConfigParagraphs cam={currentStream} />
            </>
          )}
        </div>

        {showEditors ? (
          <>
            {resolutionOptions.length > 0 && (
              <div className="pt-2">
                <h6 className="text-muted text-uppercase small mb-2">Resolution</h6>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: "0.5rem" }}>
                  <Input
                    type="select"
                    bsSize="sm"
                    style={{ maxWidth: "12rem" }}
                    value={draft.resolution ?? ""}
                    onChange={(e) => onDraftChange("resolution", e.target.value)}
                    disabled={fieldsDisabled}
                    aria-label={`Resolution for channel ${channelNumber} ${streamType}`}
                  >
                    <option value="" disabled>
                      Choose…
                    </option>
                    {resolutionOptions.map((opt) => (
                      <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                  {dirty.has("resolution") && <CurrentBadge value={deviceValues?.resolution} label="resolution" />}
                </div>
                <PerFieldStatus
                  result={applyResult?.perField?.resolution}
                  label="resolution"
                  formatApplied={(a) => (a?.w != null && a?.h != null ? `${a.w}×${a.h}` : "—")}
                />
              </div>
            )}

            {fpsOptions.length > 0 && (
              <div className="pt-2">
                <h6 className="text-muted text-uppercase small mb-2">FPS</h6>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: "0.5rem" }}>
                  <Input
                    type="select"
                    bsSize="sm"
                    style={{ maxWidth: "8rem" }}
                    value={draft.fps ?? ""}
                    onChange={(e) => onDraftChange("fps", e.target.value)}
                    disabled={fieldsDisabled}
                    aria-label={`FPS for channel ${channelNumber} ${streamType}`}
                  >
                    <option value="" disabled>
                      Choose…
                    </option>
                    {fpsOptions.map((opt) => (
                      <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                  {dirty.has("fps") && <CurrentBadge value={deviceValues?.fps} label="fps" />}
                </div>
                <PerFieldStatus
                  result={applyResult?.perField?.fps}
                  label="fps"
                  formatApplied={(a) => (a != null ? `${a} fps` : "—")}
                />
              </div>
            )}

            {hasBitrateBounds && (
              <div className="pt-2">
                <h6 className="text-muted text-uppercase small mb-2">Bitrate (kbps)</h6>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: "0.5rem" }}>
                  <Input
                    type="number"
                    bsSize="sm"
                    min={minBitrate}
                    max={maxBitrate}
                    step={1}
                    value={draft.bitrate ?? ""}
                    onChange={(e) => onDraftChange("bitrate", e.target.value)}
                    disabled={fieldsDisabled}
                    aria-label={`Bitrate kbps for channel ${channelNumber} ${streamType}`}
                    style={{ width: "8rem" }}
                  />
                  <span className="small text-muted">
                    {minBitrate} – {maxBitrate} kbps
                  </span>
                  {dirty.has("bitrate") && <CurrentBadge value={deviceValues?.bitrate} label="bitrate" />}
                </div>
                {invalid.bitrate && <p className="small text-danger mb-0 mt-1">{invalid.bitrate}</p>}
                <PerFieldStatus
                  result={applyResult?.perField?.bitrate}
                  label="bitrate"
                  formatApplied={(a) => (a != null ? `${a} kbps` : "—")}
                />
              </div>
            )}

            {showBitrateControlUnavailable ? (
              <UnavailableCapabilityRow
                label="Bitrate control"
                ariaLabel={`Bitrate control mode for channel ${channelNumber} ${streamType}`}
              />
            ) : (
              modeOptions.length > 0 && (
                <div className="pt-2">
                  <h6 className="text-muted text-uppercase small mb-2">Bitrate control</h6>
                  <div className="d-flex align-items-center flex-wrap" style={{ gap: "0.5rem" }}>
                    <Input
                      type="select"
                      bsSize="sm"
                      style={{ maxWidth: "8rem" }}
                      value={draft.bitrate_control ?? ""}
                      onChange={(e) => onDraftChange("bitrate_control", e.target.value)}
                      disabled={fieldsDisabled}
                      aria-label={`Bitrate control mode for channel ${channelNumber} ${streamType}`}
                    >
                      <option value="" disabled>
                        Choose…
                      </option>
                      {modeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                    {dirty.has("bitrate_control") && (
                      <CurrentBadge
                        value={deviceValues?.bitrate_control ? deviceValues.bitrate_control.toUpperCase() : ""}
                        label="bitrate control"
                      />
                    )}
                  </div>
                  <PerFieldStatus
                    result={applyResult?.perField?.bitrate_control}
                    label="bitrate control"
                    formatApplied={(a) => (a != null ? String(a).toUpperCase() : "—")}
                  />
                </div>
              )
            )}

            {showEncodingUnavailable ? (
              <UnavailableCapabilityRow
                label="Encoding"
                ariaLabel={`Encoding for channel ${channelNumber} ${streamType}`}
              />
            ) : (
              encodingOptions.length > 0 && (
                <div className="pt-2">
                  <h6 className="text-muted text-uppercase small mb-2">Encoding</h6>
                  <div className="d-flex align-items-center flex-wrap" style={{ gap: "0.5rem" }}>
                    <Input
                      type="select"
                      bsSize="sm"
                      style={{ maxWidth: "8rem" }}
                      value={draft.encoding ?? ""}
                      onChange={(e) => onDraftChange("encoding", e.target.value)}
                      disabled={fieldsDisabled}
                      aria-label={`Encoding for channel ${channelNumber} ${streamType}`}
                    >
                      <option value="" disabled>
                        Choose…
                      </option>
                      {encodingOptions.map((opt) => (
                        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                    {dirty.has("encoding") && <CurrentBadge value={deviceValues?.encoding ?? ""} label="encoding" />}
                  </div>
                  <PerFieldStatus
                    result={applyResult?.perField?.encoding}
                    label="encoding"
                    formatApplied={(a) => (a != null ? String(a) : "—")}
                  />
                </div>
              )
            )}

            {applyResult?.batchError && (
              <p className="small text-danger mb-0 mt-2">Refresh after Apply failed: {applyResult.batchError}</p>
            )}
          </>
        ) : null}

        <div className="mt-auto pt-3 d-flex flex-wrap align-items-center" style={{ gap: "0.5rem" }}>
          <Button
            color="primary"
            size="sm"
            disabled={!onGetCapabilities || streamLoading || dashcamBusy || !gatherLoaded || applying}
            onClick={onGetCapabilities}
            title={gatherLoaded ? undefined : TITLE_NEED_GATHER_BEFORE_CAPABILITIES}
            aria-label={`Get ${streamType} stream capabilities for channel ${channelNumber}`}
          >
            {streamLoading && <Spinner size="sm" className="mr-2" />}
            Get channel capabilities
          </Button>
          {showEditors && (
            <>
              <Button
                color="secondary"
                size="sm"
                outline
                disabled={!isDirty || applying || dashcamBusy}
                onClick={onReset}
                aria-label={`Reset channel ${channelNumber} ${streamType}`}
              >
                Reset
              </Button>
              <Button
                color="success"
                size="sm"
                disabled={!isDirty || applying || dashcamBusy || hasInvalid}
                onClick={onApply}
                aria-label={`Apply channel ${channelNumber} ${streamType}`}
              >
                {applying && <Spinner size="sm" className="mr-2" />}
                Apply
              </Button>
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
